import React, { FC, SyntheticEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MoveToIcon from '../../../../../Icons/MoveToIcon';
import useTacticKanban from '../TacticKanban.hook';
import useTacticStepControlPanelConfig from './TacticStepControlPanel.config';
import {
  StepMoveToButtonStyle,
  StepMoveToDropdownStyles,
  DependsOnSelectorStyle,
  RequiredCheckboxStyle,
} from './TacticStepControlPanel.style';
import { SelectOption } from 'basics/options/options.types';
import { Tactic } from 'generated/maya.types';
import Box from 'yoda-ui/components/Box';
import CheckBoxYoda from 'yoda-ui/components/Form/CheckBoxYoda';
import SelectYoda from 'yoda-ui/components/Form/SelectYoda';
import Menu from 'yoda-ui/components/Menu';
import { useYodaCreateForm, YodaFormProvider } from 'yoda-ui/yodaForm';
import { YodaColors, YodaSpacing } from 'yoda-ui/yodaTheme';

type TacticStepControlPanelProps = {
  actionOrder: string;
  availableSteps: string[];
  allGroupedTactics: Record<string, Tactic[]> | undefined;
  isEditingDisabled: boolean;
  isGroupMandatory: boolean;
  currentDependency: string | null;
  onReorder: (destinationStep: string) => void;
  onSetDependency: (dependsOnStep: string | null) => void;
  onSetMandatory: (isMandatory: boolean) => void;
};

const TacticStepControlPanel: FC<TacticStepControlPanelProps> = ({
  actionOrder,
  availableSteps,
  allGroupedTactics,
  isEditingDisabled,
  isGroupMandatory,
  currentDependency,
  onReorder,
  onSetDependency,
  onSetMandatory,
}) => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const yodaFormMethods = useYodaCreateForm({ setIsValidOptional: true });

  const { isEditingEnabled } = useTacticKanban();

  const {
    currentStep,
    menuItems,
    dependOnSelectorConfig,
  } = useTacticStepControlPanelConfig({
    actionOrder,
    availableSteps,
    allGroupedTactics,
    currentDependency,
    onReorder,
    setOpen,
  });

  const isDisabledMovingStep = availableSteps.length === 0;
  const isMoveToDisabled = isEditingDisabled || !isEditingEnabled || isDisabledMovingStep;

  const getAnchorPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const offset = 8;

      return {
        top: rect.bottom,
        left: rect.right - offset,
      };
    }
    return undefined;
  };

  const handleDependOnChange = (event: SyntheticEvent<Element, Event>, value: SelectOption | null) => {
    if (!value) {
      return;
    }

    const selectedStep = value.id.toString();

    if (selectedStep === 'None') {
      onSetDependency(null);
      return;
    }

    onSetDependency(selectedStep);
  };

  return (
    <>
      <Box display='flex' alignItems='center' justifyContent='flex-end'>
        <Box
          sx={ StepMoveToButtonStyle(!isMoveToDisabled) }
          ref={ buttonRef }
          onClick={ isEditingDisabled ? undefined : () => setOpen(true) }
        >
          <MoveToIcon style={
            {
              color: !isMoveToDisabled ? YodaColors.blue7 : YodaColors.gray2,
              marginRight: YodaSpacing.small,
            }
          }
          />
          <Box component="span">
            { t('maya_tactic_reordering_button_label') }
          </Box>
        </Box>
        <Menu
          anchorPosition={ open ? getAnchorPosition() : undefined }
          anchorReference="anchorPosition"
          anchorOrigin={
            {
              vertical: 'top',
              horizontal: 'center',
            }
          }
          transformOrigin={
            {
              vertical: 'top',
              horizontal: 'center',
            }
          }
          sx={ StepMoveToDropdownStyles }
          open={ open }
          onClose={ () => setOpen(false) }
          anchorEl={ buttonRef.current }
        >
          { menuItems }
        </Menu>

        <Box sx={ RequiredCheckboxStyle(isEditingEnabled) } >
          <YodaFormProvider { ...yodaFormMethods.providerFields }>
            <CheckBoxYoda
              label={ t('maya_tactic_required_checkbox_label') }
              name={ `groups.${actionOrder}.mandatory` }
              checked={ isGroupMandatory }
              onChange={
                (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                  onSetMandatory(checked);
                }
              }
              disabled={ !isEditingEnabled }
            />
          </YodaFormProvider>
        </Box>

        <Box sx={ DependsOnSelectorStyle(isEditingEnabled, currentStep) }>
          <YodaFormProvider { ...yodaFormMethods.providerFields }>
            <SelectYoda
              key={ `step-depend-on-selector-${actionOrder}-${currentDependency || 'none'}` }
              disableClearable={ true }
              { ...dependOnSelectorConfig }
              onChange={ handleDependOnChange }
              iconColor={ isEditingEnabled ? YodaColors.gray9 : YodaColors.gray2 }
            />
          </YodaFormProvider>
        </Box>
      </Box>
    </>
  );
};

export default TacticStepControlPanel;
